function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

let lastInputWasTouch = false;
let usingMouseOnTouchDevice = false;

/**
 * @param {Event} event
 */
function updateInputMethod(event) {
  if (event.type.startsWith('touch')) {
    lastInputWasTouch = true;
    usingMouseOnTouchDevice = false;
  } else if (event.type === 'mousemove' || event.type === 'mousedown') {
    if (isTouchDevice() && !lastInputWasTouch) {
      usingMouseOnTouchDevice = true;
    } else if (lastInputWasTouch) {
      usingMouseOnTouchDevice = true;
      setTimeout(() => {
        lastInputWasTouch = false;
      }, 1000);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  /** @type {HTMLElement | null} */
  const menuContainer = document.querySelector('.js-header-inline-menu');
  /** @type {HTMLElement[]} */
  const menuItems = Array.from(menuContainer?.querySelectorAll('.js-header-mega-menu-item') || []);
  const topLevelControlSelector = '.js-header-top-level-control';
  const closeTimers = new WeakMap();

  if (!menuContainer) return;

  document.addEventListener('touchstart', updateInputMethod, { passive: true });
  document.addEventListener('touchend', updateInputMethod, { passive: true });
  document.addEventListener('mousemove', updateInputMethod, { passive: true });
  document.addEventListener('mousedown', updateInputMethod, { passive: true });

  /**
   * @param {Event} event
   * @returns {Element | null}
   */
  function getEventTargetElement(event) {
    return event.target instanceof Element ? event.target : null;
  }

  /**
   * @param {Element | null} element
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  function getClosestHTMLElement(element, selector) {
    const match = element?.closest(selector);
    return match instanceof HTMLElement ? match : null;
  }

  /**
   * @param {Element | null} element
   * @param {string} selector
   * @returns {HTMLButtonElement | null}
   */
  function getClosestButton(element, selector) {
    const match = element?.closest(selector);
    return match instanceof HTMLButtonElement ? match : null;
  }

  /** @returns {HTMLElement[]} */
  const getTopLevelControls = () =>
    Array.from(menuContainer.querySelectorAll(topLevelControlSelector)).filter(
      (element) => element instanceof HTMLElement
    );

  /**
   * @param {HTMLElement | null | undefined} item
   * @returns {HTMLButtonElement | null}
   */
  const getMenuButton = (item) => item?.querySelector('.js-header-menu-toggle') ?? null;

  /**
   * @param {HTMLElement | null | undefined} item
   * @returns {HTMLElement | null}
   */
  const getMenuPanel = (item) => item?.querySelector('.js-header-mega-menu-panel') ?? null;

  /**
   * @param {HTMLElement | null | undefined} item
   * @returns {HTMLAnchorElement[]}
   */
  const getSubmenuLinks = (item) => Array.from(item?.querySelectorAll('.js-header-mega-menu-panel a[href]') || []);

  /**
   * @param {HTMLElement | null | undefined} panel
   * @returns {HTMLElement[]}
   */
  const getPanelFocusableElements = (panel) =>
    Array.from(
      panel?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
      ) || []
    ).filter((element) => element instanceof HTMLElement);

  const isMenuOpen = (item) => item?.dataset.open === 'true';

  /**
   * @param {HTMLElement | null | undefined} panel
   * @param {boolean} isInteractive
   */
  function setPanelInteractiveState(panel, isInteractive) {
    if (!panel) return;

    panel.toggleAttribute('inert', !isInteractive);

    getPanelFocusableElements(panel).forEach((element) => {
      if (isInteractive) {
        if (element.hasAttribute('data-menu-tabindex')) {
          const previousTabIndex = element.getAttribute('data-menu-tabindex');

          if (previousTabIndex === '') {
            element.removeAttribute('tabindex');
          } else if (previousTabIndex !== null) {
            element.setAttribute('tabindex', previousTabIndex);
          }

          element.removeAttribute('data-menu-tabindex');
        }

        return;
      }

      if (!element.hasAttribute('data-menu-tabindex')) {
        const currentTabIndex = element.getAttribute('tabindex');
        element.setAttribute('data-menu-tabindex', currentTabIndex ?? '');
      }

      element.setAttribute('tabindex', '-1');
    });
  }

  function syncOverlayState() {
    const hasOpenMenu = menuItems.some((item) => isMenuOpen(item));
    document.body.classList.toggle('js-mega-menu-active', hasOpenMenu);
  }

  /**
   * @param {HTMLElement | null | undefined} item
   */
  function clearCloseTimer(item) {
    if (!item) return;

    const timerId = closeTimers.get(item);

    if (timerId) {
      window.clearTimeout(timerId);
      closeTimers.delete(item);
    }
  }

  /**
   * @param {HTMLElement | null | undefined} item
   */
  function scheduleClose(item) {
    if (!item) return;

    clearCloseTimer(item);

    const timerId = window.setTimeout(() => {
      closeMenu(item);
      closeTimers.delete(item);
    }, 250);

    closeTimers.set(item, timerId);
  }

  /**
   * @param {HTMLElement | null | undefined} item
   * @param {boolean} isOpen
   */
  function setMenuState(item, isOpen) {
    const button = getMenuButton(item);
    const panel = getMenuPanel(item);

    if (!item || !button || !panel) return;

    item.dataset.open = String(isOpen);
    button.setAttribute('aria-expanded', String(isOpen));
    button.setAttribute('aria-label', isOpen ? button.dataset.closeLabel || '' : button.dataset.openLabel || '');
    panel.hidden = !isOpen;
    setPanelInteractiveState(panel, isOpen);

    syncOverlayState();
  }

  /**
   * @param {HTMLElement | null | undefined} item
   * @param {{ focusButton?: boolean }} [options]
   */
  function closeMenu(item, { focusButton = false } = {}) {
    if (!item) return;

    clearCloseTimer(item);
    setMenuState(item, false);

    if (focusButton) {
      getMenuButton(item)?.focus();
    }
  }

  /**
   * @param {{ except?: HTMLElement | null }} [options]
   */
  function closeAllMenus({ except = null } = {}) {
    menuItems.forEach((item) => {
      if (item !== except) closeMenu(item);
    });
  }

  /**
   * @param {HTMLElement | null | undefined} item
   */
  function openMenu(item) {
    if (!item) return;

    clearCloseTimer(item);
    closeAllMenus({ except: item });
    setMenuState(item, true);
  }

  menuItems.forEach((item) => {
    setPanelInteractiveState(getMenuPanel(item), isMenuOpen(item));

    item.addEventListener('mouseenter', () => {
      if (!isTouchDevice() || usingMouseOnTouchDevice) {
        openMenu(item);
      }
    });

    item.addEventListener('mouseleave', () => {
      if (!isTouchDevice() || usingMouseOnTouchDevice) {
        scheduleClose(item);
      }

      syncOverlayState();
    });

    item.addEventListener('focusout', () => {
      clearCloseTimer(item);
      setTimeout(() => {
        if (!item.contains(document.activeElement)) {
          closeMenu(item);
        }
      });
    });
  });

  menuContainer.addEventListener(
    'click',
    /** @param {MouseEvent} event */ (event) => {
      const target = getEventTargetElement(event);
      const button = getClosestButton(target, '.js-header-menu-toggle');

      if (!button || !menuContainer.contains(button)) return;

      const item = getClosestHTMLElement(button, '.js-header-mega-menu-item');

      event.preventDefault();

      if (isMenuOpen(item)) {
        closeMenu(item, { focusButton: true });
      } else {
        openMenu(item);
      }
    }
  );

  document.addEventListener(
    'click',
    /** @param {MouseEvent} event */ (event) => {
      const target = getEventTargetElement(event);

      if (!target || !menuContainer.contains(target)) {
        closeAllMenus();
      }
    }
  );

  menuContainer.addEventListener(
    'keydown',
    /** @param {KeyboardEvent} event */ (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      const target = getEventTargetElement(event);
      const currentControl = getClosestHTMLElement(target, 'a, button');

      if (!currentControl || !menuContainer.contains(currentControl)) return;

      const parentItem = getClosestHTMLElement(currentControl, '.js-header-mega-menu-item');
      const topLevelControls = getTopLevelControls();
      const topLevelIndex = topLevelControls.indexOf(currentControl);
      const submenuLinks = getSubmenuLinks(parentItem);
      const submenuIndex = currentControl instanceof HTMLAnchorElement ? submenuLinks.indexOf(currentControl) : -1;

      if (event.key === 'Escape' && parentItem && isMenuOpen(parentItem)) {
        event.preventDefault();
        closeMenu(parentItem, { focusButton: true });
        return;
      }

      if (submenuIndex !== -1) {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          if (submenuIndex < submenuLinks.length - 1) {
            event.preventDefault();
            submenuLinks[submenuIndex + 1].focus();
          }
          return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          if (submenuIndex > 0) {
            event.preventDefault();
            submenuLinks[submenuIndex - 1].focus();
          }
          return;
        }

        if (event.key === 'Home' && submenuLinks.length) {
          event.preventDefault();
          submenuLinks[0].focus();
          return;
        }

        if (event.key === 'End' && submenuLinks.length) {
          event.preventDefault();
          submenuLinks[submenuLinks.length - 1].focus();
          return;
        }
      }

      if (
        currentControl.classList.contains('js-header-menu-toggle') &&
        isMenuOpen(parentItem) &&
        (event.key === 'ArrowRight' || event.key === 'ArrowDown') &&
        submenuLinks.length
      ) {
        event.preventDefault();
        submenuLinks[0].focus();
        return;
      }

      if (topLevelIndex === -1) return;

      if ((event.key === 'ArrowRight' || event.key === 'ArrowDown') && topLevelIndex < topLevelControls.length - 1) {
        event.preventDefault();
        topLevelControls[topLevelIndex + 1].focus();
        return;
      }

      if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && topLevelIndex > 0) {
        event.preventDefault();
        topLevelControls[topLevelIndex - 1].focus();
        return;
      }

      if (event.key === 'Home' && topLevelControls.length) {
        event.preventDefault();
        topLevelControls[0].focus();
        return;
      }

      if (event.key === 'End' && topLevelControls.length) {
        event.preventDefault();
        topLevelControls[topLevelControls.length - 1].focus();
      }
    }
  );
});
